import axios, { AxiosInstance, AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import env from '../../settings/env';
import { store } from '../../store';
import { clear } from '../../store/slices/auth';
import { toast } from '../../hooks/use-toast';
import { markHandled } from './errors';

/**
 * Cliente Axios configurado para fazer requisições à API
 */
class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: env.PUBLIC_API_URL,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    // Request interceptor — injeta token do Redux store
    this.client.interceptors.request.use(
      (config) => {
        const token = store.getState().auth?.token;
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor
    this.client.interceptors.response.use(
      (response: AxiosResponse) => response,
      async (error: AxiosError) => {
        if (error.response) {
          const status = error.response.status;
          const isAuthPage =
            window.location.pathname.startsWith('/login') ||
            window.location.pathname.startsWith('/auth');

          switch (status) {
            case 409: {
              const data = error.response.data as Record<string, unknown> | undefined;
              const msg = typeof data?.error === 'string' ? data.error : '';
              if (msg.toLowerCase().includes('currently running')) {
                toast({
                  title: 'Simulação em andamento',
                  description: 'Aguarde a conclusão da simulação antes de realizar esta ação.',
                  variant: 'destructive',
                });
                return Promise.reject(
                  markHandled(new Error('Simulação em andamento. Aguarde a conclusão antes de realizar esta ação.')),
                );
              }
              break;
            }
            case 400:
            case 401:
              if (!isAuthPage) {
                store.dispatch(clear());
                window.location.href = '/login?session_expired=1';
              }
              break;
            case 403:
              console.error('Acesso negado:', error);
              break;
            case 500:
              console.error('Erro interno do servidor:', error);
              break;
          }
        } else if (error.request) {
          console.error('Erro de rede:', error.message);
        }

        return Promise.reject(error);
      }
    );
  }

  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.get<T>(url, config);
    return response.data;
  }

  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.post<T>(url, data, config);
    return response.data;
  }

  async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.put<T>(url, data, config);
    return response.data;
  }

  async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.patch<T>(url, data, config);
    return response.data;
  }

  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.delete<T>(url, config);
    return response.data;
  }

  getInstance(): AxiosInstance {
    return this.client;
  }
}

export const apiClient = new ApiClient();
export default apiClient;
